import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { formatUTCDateWithTZ } from '../../../utils/timezone';
import { fetchAllRestaurantMenuItems } from '../../services/Menu';
import { fetchAllRestaurants } from '../../services/Asset';
import { Restaurant } from '../../types/Asset';
import UserContext from '../../../contexts/UserContext';
import sendToast from '../../../utils/Toast';
import { customDateTimeFormat } from '../../../utils';
import { ServicesType } from '../../types/ServiceInstance';
import { ServiceAddOn } from '../../types/Service';
import { getMealMeta, groupByMeal, MEAL_FILTER_OPTIONS, getDietTags, findConflictingMealCategory, mealMixErrorMessage, MAX_ORDER_QUANTITY, addonSignature } from './diningMenuUtils';
import { createDiningOrder } from '../../services/OrderStatus';
import DishDetailModal, { DishDetailViewModel } from './DishDetailModal';
import PlaceOrderPage, { FulfillmentMethod, PaymentConsentsByCartId, PlaceOrderEntry } from './PlaceOrderPage';
import {
    calendarIcon as CalendarIcon,
    closeIcon as CloseIcon,
    mealFilterIcon as MealFilterIcon,
    plusIcon as PlusIcon,
    restaurantIcon as RestaurantIcon,
    searchIcon as SearchIcon,
    utensilsIcon as UtensilsIcon,
} from './DiningMenuIcons';
import './dining-page.less';

interface Props {
    facilityTimezone: string;
}

interface DateSelectButtonProps {
    value?: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    className?: string;
}

type OrderCartEntry = PlaceOrderEntry;

const DateSelectButton = React.forwardRef<HTMLButtonElement, DateSelectButtonProps>(
    ({ value, onClick, className }, ref) => (
        <button
            type="button"
            className={`date-select-btn${className ? ` ${className}` : ''}`}
            onClick={onClick}
            ref={ref}
            aria-label="Menu date"
        >
            <CalendarIcon />
            <span>{value}</span>
        </button>
    ),
);
DateSelectButton.displayName = 'DateSelectButton';

const makeCartId = (itemId: string) =>
    `${itemId}__${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const MenuDining = ({ facilityTimezone }: Props) => {
    const [calendarDate, setCalendarDate] = useState<Date>(new Date());
    const [menuItems, setMenuItems] = useState<ServicesType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedMenu, setSelectedMenu] = useState<ServicesType | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [mealFilter, setMealFilter] = useState<string>('all');
    const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState<string>('all');
    const [orderItems, setOrderItems] = useState<Map<string, OrderCartEntry>>(new Map());
    const [reviewOpen, setReviewOpen] = useState(false);
    const [placingOrder, setPlacingOrder] = useState(false);
    const loadedForFacilityRef = useRef<string | null>(null);
    const restaurantsRef = useRef<Restaurant[]>([]);

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const correspondingSpeak2Resident = listOfResidents[activeRegistrantsIndexes[0]];

    useLayoutEffect(() => {
        const shell = document.querySelector('.neighbors-interior-shell');
        if (!shell) return undefined;
        shell.classList.add('neighbors-interior-shell--dining');
        return () => {
            shell.classList.remove('neighbors-interior-shell--dining');
        };
    }, []);

    const roomCategory = correspondingSpeak2Resident.Unit?.RoomCategory;

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                const facility = correspondingSpeak2Resident.Facility;
                let list = restaurantsRef.current;
                if (loadedForFacilityRef.current !== facility) {
                    list = await fetchAllRestaurants({ Facility: facility });
                    setRestaurants(list);
                    restaurantsRef.current = list;
                    loadedForFacilityRef.current = facility;
                }

                const restaurantFilter =
                    selectedRestaurant !== 'all' && list.some((r) => r._id === selectedRestaurant)
                        ? selectedRestaurant
                        : '';

                const startDate = moment(calendarDate).startOf('day').format(customDateTimeFormat);
                const endDate = moment(calendarDate).endOf('day').format(customDateTimeFormat);
                const filter = {
                    Facility: facility,
                    startDate,
                    endDate,
                    ...(roomCategory && { calendarType: [roomCategory.toString()] }),
                    ...(restaurantFilter && { Asset: restaurantFilter }),
                };
                const response = await fetchAllRestaurantMenuItems({ filter });
                setMenuItems(response);
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'Failed to fetch menu');
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [calendarDate, selectedRestaurant, correspondingSpeak2Resident.Facility, roomCategory]);

    const tz = facilityTimezone || '';
    const formattedDate = formatUTCDateWithTZ(calendarDate.getTime(), 'dddd, MMMM D, YYYY', tz);
    const isToday = formatUTCDateWithTZ(calendarDate.getTime(), 'YYYY-MM-DD', tz) === formatUTCDateWithTZ(Date.now(), 'YYYY-MM-DD', tz);

    const orderEntries = useMemo(() => Array.from(orderItems.values()), [orderItems]);
    const orderCount = orderEntries.reduce((sum, entry) => sum + entry.quantity, 0);

    const itemCountInCart = (itemId: string) =>
        orderEntries.reduce((count, entry) => (entry.itemId === itemId ? count + entry.quantity : count), 0);

    const selectedDish: DishDetailViewModel | null = selectedMenu
        ? {
              id: selectedMenu._id,
              name: selectedMenu.name,
              mealTypeLabel: getMealMeta((selectedMenu.menuCategory || '').toLowerCase().trim() || 'all day').label,
              restaurantName: restaurants.find((r) => r._id === selectedMenu.Asset)?.AssetName || '',
              shortDescription: selectedMenu.shortDescription,
              longDescription: selectedMenu.longDescription,
              imageUrl: selectedMenu.image && selectedMenu.signedUrl ? selectedMenu.signedUrl : undefined,
              itemCost: selectedMenu.defaults?.externalCost
                  ? Number(selectedMenu.defaults.externalCost) || 0
                  : 0,
              tags: getDietTags(selectedMenu),
              addons: selectedMenu.ServiceAddOns || [],
          }
        : null;

    const getMealMixConflict = (item: ServicesType, excludeCartId?: string) =>
        findConflictingMealCategory(
            item.menuCategory,
            orderEntries
                .filter((entry) => entry.cartId !== excludeCartId)
                .map((entry) => entry.item.menuCategory),
        );

    const closeDishModal = useCallback(() => setSelectedMenu(null), []);

    const openDish = (item: ServicesType) => {
        setSelectedMenu(item);
    };

    const getOrderBlockedMessage = useCallback(
        (replaceCartId?: string) => {
            if (!selectedMenu) return undefined;
            const conflict = findConflictingMealCategory(
                selectedMenu.menuCategory,
                orderEntries
                    .filter((entry) => entry.cartId !== replaceCartId)
                    .map((entry) => entry.item.menuCategory),
            );
            return conflict ? mealMixErrorMessage(selectedMenu.menuCategory, conflict) : undefined;
        },
        [selectedMenu, orderEntries],
    );

    const addItemToOrder = (
        item: ServicesType,
        addons?: ServiceAddOn[],
        quantity = 1,
        replaceCartId?: string,
    ): boolean => {
        const conflict = getMealMixConflict(item, replaceCartId);
        if (conflict) {
            sendToast('error', mealMixErrorMessage(item.menuCategory, conflict));
            return false;
        }

        const selectedAddons =
            addons ?? (item.ServiceAddOns || []).filter((addon) => addon.required);
        const nextQty = Math.min(MAX_ORDER_QUANTITY, Math.max(1, quantity));
        const restaurantName = restaurants.find((r) => r._id === item.Asset)?.AssetName || '';
        const incomingKey = addonSignature(selectedAddons);

        setOrderItems((prev) => {
            const next = new Map(prev);

            if (replaceCartId && next.has(replaceCartId)) {
                const editing = next.get(replaceCartId)!;
                const otherMatch = Array.from(next.values()).find(
                    (entry) =>
                        entry.cartId !== replaceCartId &&
                        entry.itemId === item._id &&
                        addonSignature(entry.addons) === incomingKey,
                );
                if (otherMatch) {
                    next.set(otherMatch.cartId, {
                        ...otherMatch,
                        quantity: Math.min(MAX_ORDER_QUANTITY, otherMatch.quantity + nextQty),
                    });
                    next.delete(replaceCartId);
                    return next;
                }
                next.set(replaceCartId, {
                    ...editing,
                    addons: selectedAddons,
                    quantity: nextQty,
                });
                return next;
            }

            const existing = Array.from(next.values()).find(
                (entry) => entry.itemId === item._id && addonSignature(entry.addons) === incomingKey,
            );
            if (existing) {
                next.set(existing.cartId, {
                    ...existing,
                    quantity: Math.min(MAX_ORDER_QUANTITY, existing.quantity + nextQty),
                });
                return next;
            }

            const cartId = makeCartId(item._id);
            next.set(cartId, {
                cartId,
                itemId: item._id,
                name: item.name,
                addons: selectedAddons,
                item,
                restaurantName,
                quantity: nextQty,
            });
            return next;
        });
        return true;
    };

    /** List + : open modal for a first add; once in cart, increment the latest variant by 1. */
    const quickAddFromList = (item: ServicesType) => {
        const existingLines = orderEntries.filter((entry) => entry.itemId === item._id);
        if (existingLines.length === 0) {
            openDish(item);
            return;
        }

        const latest = existingLines[existingLines.length - 1];
        if (latest.quantity >= MAX_ORDER_QUANTITY) {
            sendToast('error', `You can order at most ${MAX_ORDER_QUANTITY} of this item.`);
            return;
        }

        addItemToOrder(item, latest.addons, 1);
    };

    const removeOrderItem = (cartId: string) => {
        setOrderItems((prev) => {
            const next = new Map(prev);
            next.delete(cartId);
            if (next.size === 0) setReviewOpen(false);
            return next;
        });
    };

    const toggleOrderAddon = (cartId: string, addon: ServiceAddOn) => {
        if (addon.required) return;

        setOrderItems((prev) => {
            const next = new Map(prev);
            const entry = next.get(cartId);
            if (!entry) return prev;

            const isSelected = entry.addons.some((selected) => selected.id === addon.id);
            const nextAddons = isSelected
                ? entry.addons.filter((selected) => selected.id !== addon.id)
                : [...entry.addons, addon];
            const nextKey = addonSignature(nextAddons);
            const otherMatch = Array.from(next.values()).find(
                (other) =>
                    other.cartId !== cartId &&
                    other.itemId === entry.itemId &&
                    addonSignature(other.addons) === nextKey,
            );

            if (otherMatch) {
                next.set(otherMatch.cartId, {
                    ...otherMatch,
                    quantity: otherMatch.quantity + entry.quantity,
                });
                next.delete(cartId);
                return next;
            }

            next.set(cartId, { ...entry, addons: nextAddons });
            return next;
        });
    };

    const changeOrderQuantity = (cartId: string, quantity: number) => {
        if (quantity <= 0) {
            removeOrderItem(cartId);
            return;
        }
        setOrderItems((prev) => {
            const next = new Map(prev);
            const existing = next.get(cartId);
            if (!existing) return prev;
            next.set(cartId, {
                ...existing,
                quantity: Math.min(MAX_ORDER_QUANTITY, Math.max(1, quantity)),
            });
            return next;
        });
    };

    const placeOrder = async ({
        orderType,
        specialNotes,
        paymentConsents,
    }: {
        orderType: FulfillmentMethod;
        specialNotes: string;
        paymentConsents?: PaymentConsentsByCartId;
    }): Promise<boolean> => {
        if (!orderEntries.length) return false;
        if (!correspondingSpeak2Resident.Facility || !correspondingSpeak2Resident._id) {
            sendToast('error', 'Unable to place order for this resident.');
            return false;
        }

        try {
            setPlacingOrder(true);
            const mealItems = orderEntries.map((entry) => ({
                serviceInstanceId: entry.itemId,
                serviceAddOns: entry.addons,
                quantity: entry.quantity,
                ...(paymentConsents && paymentConsents[entry.cartId] && { paymentConsent: paymentConsents[entry.cartId] }),
            }));
            await createDiningOrder(correspondingSpeak2Resident.Facility, {
                Facility: correspondingSpeak2Resident.Facility,
                ResidentId: String(correspondingSpeak2Resident._id),
                specialNotes,
                orderType,
                mealItems,
            });
            setOrderItems(new Map());
            return true;
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Error while placing order');
            return false;
        } finally {
            setPlacingOrder(false);
        }
    };

    const filteredMenuItems = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return menuItems;
        return menuItems.filter((item) => {
            const searchableText = `${item.name || ''} ${item.shortDescription || ''}`.toLowerCase();
            return searchableText.includes(query);
        });
    }, [menuItems, searchQuery]);

    const mealGroups = useMemo(() => {
        const groups = groupByMeal(filteredMenuItems);
        if (mealFilter === 'all') return groups;
        return groups.filter((group) => group.mealKey === mealFilter);
    }, [filteredMenuItems, mealFilter]);

    return (
        <div className={`dining-menu-page${orderCount > 0 ? ' has-order-bar' : ''}`}>
            <div className="hero">
                <div className="hero-left">
                    <div className="hero-icon-badge" aria-hidden="true">
                        <UtensilsIcon />
                    </div>
                    <div>
                        <div className="hero-eyebrow">Dining</div>
                        <h1 className="hero-title">
                            {isToday ? "Today's " : ''}
                            <em>Menu</em>
                        </h1>
                    </div>
                </div>

                <div className="hero-date-badge">
                    <div className="hero-date-icon" aria-hidden="true">
                        <CalendarIcon />
                    </div>
                    <div className="hero-date-text">
                        {isToday ? <div className="hero-date-label">Today</div> : null}
                        <div className="hero-date-value">{formattedDate}</div>
                    </div>
                </div>
            </div>

            <div className="filter-toolbar">
                <div className="filter-toolbar-inner">
                    <div className={`search-box${searchQuery ? ' has-value' : ''}`}>
                        <SearchIcon className="filter-control-icon" />
                        <input
                            type="text"
                            placeholder="Search for a meal..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            aria-label="Search for a meal"
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="search-clear"
                                onClick={() => setSearchQuery('')}
                                aria-label="Clear search"
                            >
                                <CloseIcon />
                            </button>
                        )}
                    </div>

                    <div className="filter-select-wrap meal-wrap">
                        <MealFilterIcon className="filter-control-icon" />
                        <select
                            value={mealFilter}
                            onChange={(e) => setMealFilter(e.target.value)}
                            aria-label="Meal type"
                        >
                            {MEAL_FILTER_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-select-wrap restaurant-wrap">
                        <RestaurantIcon className="filter-control-icon" />
                        <select
                            value={selectedRestaurant}
                            onChange={(e) => setSelectedRestaurant(e.target.value)}
                            aria-label="Restaurant"
                        >
                            <option value="all">All Restaurants</option>
                            {restaurants.map((r) => (
                                <option key={r._id} value={r._id}>
                                    {r.AssetName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-select-wrap date-wrap">
                        <DatePicker
                            selected={calendarDate}
                            onChange={(date: Date | null) => {
                                if (!date) return;
                                setCalendarDate(date);
                                setOrderItems(new Map());
                            }}
                            dateFormat="MMM d, yyyy"
                            customInput={<DateSelectButton />}
                            shouldCloseOnSelect
                        />
                    </div>
                </div>
            </div>

            <div className="menu-content">
                <div className="menu-col">
                    {loading && (
                        <div className="menu-col-loading" role="status" aria-live="polite">
                            <Loader active inline="centered" />
                        </div>
                    )}

                    {!loading && menuItems.length === 0 && (
                        <div className="menu-col--empty">
                            <p>No Meal items available for the selected date.</p>
                        </div>
                    )}

                    {!loading && menuItems.length > 0 && filteredMenuItems.length === 0 && (
                        <div className="menu-col--empty">
                            <p>No meal items match your search.</p>
                        </div>
                    )}

                    {!loading && filteredMenuItems.length > 0 && mealGroups.length === 0 && (
                        <div className="menu-col--empty">
                            <p>No meal items match the selected meal type.</p>
                        </div>
                    )}

                    {!loading && mealGroups.map(({ mealKey, items }, sectionIndex) => {
                        const meta = getMealMeta(mealKey);

                        return (
                            <section
                                key={mealKey}
                                className="meal-section"
                                style={{ animationDelay: `${0.05 + sectionIndex * 0.1}s` }}
                            >
                                <div className="course-header">
                                    <div className={`course-icon-wrap ${meta.iconClass}`}>
                                        <meta.Icon />
                                    </div>
                                    <h2 className="course-title">{meta.label}</h2>
                                    <div className="course-rule" />
                                </div>

                                <div className="menu-items">
                                    {items.map((item) => {
                                        const qty = itemCountInCart(item._id);
                                        const tags = getDietTags(item);
                                        return (
                                            <div
                                                key={item._id}
                                                className="menu-item"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => openDish(item)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openDish(item);
                                                    }
                                                }}
                                            >
                                                {item.signedUrl && (
                                                    <div className="item-image">
                                                        <img src={item.signedUrl} alt="" />
                                                    </div>
                                                )}
                                                <div className="item-body">
                                                    <div className="item-name">{item.name}</div>
                                                    {item.shortDescription && (
                                                        <div className="item-desc">{item.shortDescription}</div>
                                                    )}
                                                    {tags.length > 0 && (
                                                        <div className="item-tags">
                                                            {tags.map((tag) => (
                                                                <span key={tag.key} className={`item-tag ${tag.className || 'tag-neutral'}`}>
                                                                    {tag.label}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="item-order-btn-wrap">
                                                    <button
                                                        type="button"
                                                        className={`item-order-btn${qty > 0 ? ' added' : ''}`}
                                                        aria-label={`Add ${item.name} to order`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            quickAddFromList(item);
                                                        }}
                                                    >
                                                        <PlusIcon />
                                                    </button>
                                                    <span className={`item-qty-badge${qty > 0 ? ' show' : ''}`}>
                                                        {qty}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}
                </div>
            </div>

            {orderCount > 0
                ? createPortal(
                    <div className="view-order-bar show">
                        <div className="view-order-count">
                            <span className="badge">{orderCount}</span>
                            <span>{orderCount === 1 ? 'item in your order' : 'items in your order'}</span>
                        </div>
                        <button type="button" className="view-order-btn" onClick={() => setReviewOpen(true)}>
                            View Order
                        </button>
                    </div>,
                    document.body,
                )
                : null}

            <DishDetailModal
                open={Boolean(selectedMenu)}
                dish={selectedDish}
                getOrderBlockedMessage={getOrderBlockedMessage}
                onClose={closeDishModal}
                onAddToOrder={(addons, quantity, replaceCartId) => {
                    if (!selectedMenu) return false;
                    return addItemToOrder(selectedMenu, addons, quantity, replaceCartId);
                }}
            />

            <PlaceOrderPage
                open={reviewOpen}
                entries={orderEntries}
                placing={placingOrder}
                onClose={() => setReviewOpen(false)}
                onRemove={removeOrderItem}
                onChangeQuantity={changeOrderQuantity}
                onToggleAddon={toggleOrderAddon}
                onPlaceOrder={placeOrder}
            />
        </div>
    );
};

export default MenuDining;
